import { useMemo, useState } from 'react';
import { useBiometrics } from 'hooks/useBiometrics';
import { useBiometricDownload } from 'hooks/useBiometricDownload';
import { ReadingRow } from 'components/ProgressTab/WidgetDetail/WidgetReadingList';
import { DateRangePickerProps } from 'components/DateRangePicker/DateRangePicker';
import {
  pageOfReadings,
  range,
  toCompleteReadings,
} from 'features/progress/utils/bloodPressureUtils';
import {
  formatDateRangeLabel,
  formatTimeOfDay,
  getWeekStart,
  longFormatDate,
  parseLocalDateString,
  shortChartLabel,
  startOfToday,
} from 'utils/dateUtils';

export interface UseBloodPressureDetailsResult {
  dateRange: DateRangePickerProps;
  dates: string[];
  systolic: number[];
  diastolic: number[];
  systolicRange: string;
  diastolicRange: string;
  units: string;
  readings: ReadingRow[];
  isLoading: boolean;
  isError: boolean;
  download: () => void;
  isDownloading: boolean;
}

export const useBloodPressureDetails = (userId?: number): UseBloodPressureDetailsResult => {
  const [pageIndex, setPageIndex] = useState(0);
  // All history, fetched once: pages are sets of 7 readings, not date ranges.
  const { bloodPressure } = useBiometrics(userId);

  const units = bloodPressure.data?.units ?? 'mmHg';

  const allReadings = useMemo(
    () => toCompleteReadings(bloodPressure.data?.data),
    [bloodPressure.data],
  );
  const {
    items: windowReadings,
    hasOlder,
    hasNewer,
  } = useMemo(() => pageOfReadings(allReadings, pageIndex), [allReadings, pageIndex]);

  const today = startOfToday();
  const label = windowReadings.length
    ? formatDateRangeLabel(
        parseLocalDateString(windowReadings[0].date),
        parseLocalDateString(windowReadings[windowReadings.length - 1].date),
      )
    : formatDateRangeLabel(getWeekStart(today), today);

  const readings = useMemo<ReadingRow[]>(
    () =>
      [...windowReadings].reverse().map((reading) => ({
        id: reading.id,
        value: `${reading.systolic}/${reading.diastolic}`,
        unit: units,
        dateLabel: longFormatDate(reading.timestamp),
        time: formatTimeOfDay(reading.timestamp),
      })),
    [windowReadings, units],
  );

  const systolic = windowReadings.map((reading) => reading.systolic);
  const diastolic = windowReadings.map((reading) => reading.diastolic);

  const { download, isDownloading } = useBiometricDownload('blood_pressure', userId ?? 0);

  return {
    dateRange: {
      label,
      onPrevious: () => setPageIndex((prev) => prev + 1),
      onNext: () => setPageIndex((prev) => prev - 1),
      disablePrevious: !hasOlder,
      disableNext: !hasNewer,
    },
    dates: windowReadings.map((reading) => shortChartLabel(reading.date)),
    systolic,
    diastolic,
    systolicRange: range(systolic),
    diastolicRange: range(diastolic),
    units,
    readings,
    isLoading: bloodPressure.isLoading,
    isError: bloodPressure.isError,
    download,
    isDownloading,
  };
};
